/**
 * PropositoPage - Página completa do Propósito HeveaTalent
 * Internacionalizado (PT/EN/ES)
 */

import React, { useEffect, useRef, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { 
  Scale, Shield, Sparkles, TrendingUp, Award, Users, 
  ArrowLeft, Medal, ShoppingBag, MessageCircle, BarChart3,
  Briefcase, Heart, Leaf, CheckCircle, Send, Mail, FileText, ShieldCheck
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "../translations";
import LanguageSelector from "../components/LanguageSelector";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1c9246af-b066-494f-86fd-6acac840ed20/images/9e8c2dc08429bcc172c11cfa2a512192077dda21591c3e401673a3ab00e83663.png";

const useFadeIn = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("animate-in");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useFadeIn();
  return (
    <div
      ref={ref}
      className={`opacity-0 translate-y-6 transition-all duration-700 ease-out ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const PropositoPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <style>{`.animate-in { opacity: 1 !important; transform: translateY(0) !important; }`}</style>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-green-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 text-green-800 hover:text-green-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold">HeveaTalent</span>
            </div>
          </Link>
          <LanguageSelector />
        </div>
      </header>

      {/* Hero with Image */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Seringal" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        <div className="relative container mx-auto px-4 py-28 md:py-36 text-center">
          <p className="text-green-300 font-semibold text-sm tracking-widest uppercase mb-4">{t('purposeLabel')}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t('purposeTitle')}
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">{t('purposeSubtitle')}</p>
        </div>
      </section>

      {/* Números de Impacto */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <FadeIn>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
              <div>
                <p className="text-3xl md:text-4xl font-bold text-green-700">14</p>
                <p className="text-sm text-gray-500 mt-1">{t('purposeStatCriteria')}</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-amber-600">3</p>
                <p className="text-sm text-gray-500 mt-1">{t('purposeStatSeals')}</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-blue-600">100%</p>
                <p className="text-sm text-gray-500 mt-1">{t('purposeStatOfficial')}</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-emerald-600">{t('purposeStatAll')}</p>
                <p className="text-sm text-gray-500 mt-1">{t('purposeStatChain')}</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Missão e Visão */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <FadeIn delay={0}>
              <Card className="p-8 border-l-4 border-l-green-600 h-full">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-green-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('purposeMission')}</h2>
                <p className="text-gray-600 leading-relaxed text-base">{t('purposeMissionText')}</p>
              </Card>
            </FadeIn>
            <FadeIn delay={150}>
              <Card className="p-8 border-l-4 border-l-emerald-600 h-full">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-emerald-700" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('purposeVision')}</h2>
                <p className="text-gray-600 leading-relaxed text-base">{t('purposeVisionText')}</p>
              </Card>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Tripé HeveaTalent */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="text-gray-500 font-medium text-sm tracking-widest uppercase mb-3">{t('purposeValuesLabel')}</p>
              <h2 className="text-3xl font-bold text-gray-900">{t('purposeValuesTitle')}</h2>
              <p className="text-gray-600 mt-3 max-w-xl mx-auto">{t('purposeValuesSubtitle')}</p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { key: 'Justice', color: 'amber', Icon: Scale, items: ['purposeJustice1','purposeJustice2','purposeJustice3'] },
              { key: 'Security', color: 'blue', Icon: Shield, items: ['purposeSecurity1','purposeSecurity2','purposeSecurity3'] },
              { key: 'Simplicity', color: 'green', Icon: Sparkles, items: ['purposeSimplicity1','purposeSimplicity2','purposeSimplicity3'] },
            ].map((v, i) => (
              <FadeIn key={v.key} delay={i * 150}>
                <div className={`bg-white rounded-2xl p-8 border-t-4 border-t-${v.color}-500 shadow-sm h-full`}>
                  <div className={`w-16 h-16 bg-${v.color}-50 rounded-2xl flex items-center justify-center mb-6`}>
                    <v.Icon className={`w-8 h-8 text-${v.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t(`purpose${v.key}`)}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{t(`purpose${v.key}Desc`)}</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {v.items.map(item => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 text-${v.color}-500 mt-0.5 shrink-0`} />
                        {t(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="text-gray-500 font-medium text-sm tracking-widest uppercase mb-3">{t('purposeDiffLabel')}</p>
              <h2 className="text-3xl font-bold text-gray-900">{t('purposeDiffTitle')}</h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { key: 'Cert', Icon: Medal, bg: 'amber' },
              { key: 'Market', Icon: ShoppingBag, bg: 'blue' },
              { key: 'Jobs', Icon: Briefcase, bg: 'green' },
              { key: 'Comm', Icon: MessageCircle, bg: 'purple' },
              { key: 'Quotes', Icon: BarChart3, bg: 'teal' },
              { key: 'Rec', Icon: Heart, bg: 'rose' },
            ].map((d, i) => (
              <FadeIn key={d.key} delay={(i % 3) * 100}>
                <div className={`flex gap-4 p-5 rounded-xl bg-${d.bg}-50 border border-${d.bg}-100`} data-testid={`diferencial-${d.key.toLowerCase()}`}>
                  <div className={`w-10 h-10 bg-${d.bg}-100 rounded-lg flex items-center justify-center shrink-0`}>
                    <d.Icon className={`w-5 h-5 text-${d.bg}-700`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{t(`purposeDiff${d.key}`)}</h4>
                    <p className="text-gray-600 text-xs mt-1">{t(`purposeDiff${d.key}Desc`)}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-green-900/85"></div>
        </div>
        <div className="relative container mx-auto px-4 text-center">
          <Leaf className="w-10 h-10 text-green-300 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl text-white font-light italic max-w-3xl mx-auto leading-relaxed mb-8">
            "{t('purposeQuote')}"
          </blockquote>
          <div className="w-16 h-0.5 bg-green-400 mx-auto mb-8"></div>
          <Link 
            to="/"
            className="inline-flex items-center px-8 py-3 bg-white text-green-800 font-semibold rounded-lg hover:bg-green-50 transition-colors"
          >
            {t('purposeCTA')}
          </Link>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactSection t={t} />

      {/* Institutional Footer */}
      <InstitutionalFooter t={t} />
    </div>
  );
};

const ContactSection = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contato" className="py-16 bg-gray-50" data-testid="contact-section">
      <div className="container mx-auto px-4 max-w-2xl">
        <FadeIn>
          <div className="text-center mb-10">
            <Mail className="w-10 h-10 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900">{t('contactTitle') || 'Fale Conosco'}</h2>
            <p className="text-gray-600 mt-2">{t('contactSubtitle') || 'Envie sua dúvida, sugestão ou parceria.'}</p>
          </div>
        </FadeIn>

        {status === "success" ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="contact-success">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-800 mb-2">{t('contactSuccessTitle') || 'Mensagem Enviada!'}</h3>
            <p className="text-green-700">{t('contactSuccessMsg') || 'Retornaremos em breve pelo e-mail informado.'}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-name">{t('contactName') || 'Nome'}</Label>
                <Input id="contact-name" data-testid="contact-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              </div>
              <div>
                <Label htmlFor="contact-email">{t('contactEmail') || 'E-mail'}</Label>
                <Input id="contact-email" data-testid="contact-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
            </div>
            <div>
              <Label htmlFor="contact-subject">{t('contactSubject') || 'Assunto'}</Label>
              <Input id="contact-subject" data-testid="contact-subject" required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} />
            </div>
            <div>
              <Label htmlFor="contact-message">{t('contactMessage') || 'Mensagem'}</Label>
              <Textarea id="contact-message" data-testid="contact-message" rows={5} required value={form.message} onChange={e => setForm({...form, message: e.target.value})} />
            </div>
            {status === "error" && (
              <p className="text-red-600 text-sm" data-testid="contact-error">{t('contactError') || 'Erro ao enviar. Tente novamente.'}</p>
            )}
            <Button type="submit" disabled={status === "sending"} className="w-full bg-green-700 hover:bg-green-800" data-testid="contact-submit">
              <Send className="w-4 h-4 mr-2" />
              {status === "sending" ? (t('sending') || 'Enviando...') : (t('contactSend') || 'Enviar Mensagem')}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

const InstitutionalFooter = ({ t }) => (
  <footer className="bg-gray-900 text-gray-400 py-12" data-testid="institutional-footer">
    <div className="container mx-auto px-4">
      <div className="grid md:grid-cols-3 gap-8 mb-8">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="text-white text-lg font-bold">HeveaTalent</span>
          </div>
          <p className="text-sm leading-relaxed">{t('footerAbout') || 'Plataforma de gestão de desempenho e conexão para profissionais e proprietários na heveicultura.'}</p>
        </div>

        {/* Links */}
        <div>
          <h4 className="text-white font-semibold mb-3">{t('footerLinks') || 'Links'}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-green-400 transition-colors">{t('footerHome') || 'Início'}</Link></li>
            <li><Link to="/proposito" className="hover:text-green-400 transition-colors">{t('footerPurpose') || 'Propósito'}</Link></li>
            <li><a href="#contato" className="hover:text-green-400 transition-colors">{t('footerContact') || 'Contato'}</a></li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h4 className="text-white font-semibold mb-3">{t('footerLegal') || 'Legal'}</h4>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              <Link to="/termos" className="hover:text-green-400 transition-colors" data-testid="footer-terms-link">{t('termsTitle') || 'Termos de Uso'}</Link>
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              <Link to="/privacidade" className="hover:text-green-400 transition-colors" data-testid="footer-privacy-link">{t('privacyTitle') || 'Política de Privacidade'}</Link>
            </li>
          </ul>
          <p className="text-xs mt-4">Email: heveatalent@gmail.com</p>
        </div>
      </div>

      <div className="border-t border-gray-800 pt-6 text-center text-sm">
        <p>© 2025-2026 HeveaTalent. {t('footerRights') || 'Todos os direitos reservados.'}</p>
        <p className="text-xs mt-1 text-gray-500">{t('purposeFooterSub')}</p>
      </div>
    </div>
  </footer>
);

export default PropositoPage;
